import { useState, useEffect } from 'react'
import { useAuth } from '../context/auth'
import { supabase } from '../lib/supabase'
import { CheckCircle, AlertCircle, X, Loader2, Receipt, Download, Shield, Info, Smartphone, Wallet, CreditCard } from 'lucide-react'
import jsPDF from 'jspdf'

interface TowerPaymentConfirmationProps {
  jobId: string
  amount: number
  customerName: string
  onClose: () => void
  onConfirmationComplete: () => void
  jobPaymentMethod?: string
}

export default function TowerPaymentConfirmation({ jobId, amount, customerName, onClose, onConfirmationComplete, jobPaymentMethod }: TowerPaymentConfirmationProps) {
  const { profile } = useAuth()
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirmed, setConfirmed] = useState(false)
  const [receiptData, setReceiptData] = useState<any>(null)
  const [paymentMethod, setPaymentMethod] = useState<'mpesa' | 'cash' | 'transfer'>('cash')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [customerPhone, setCustomerPhone] = useState('')

  useEffect(() => {
    const fetchCustomerPhone = async () => {
      try {
        const { data: job } = await supabase
          .from('tow_jobs')
          .select('customer_id')
          .eq('id', jobId)
          .single()

        if (job) {
          const { data: customer } = await supabase
            .from('profiles')
            .select('phone')
            .eq('id', job.customer_id)
            .single()

          if (customer?.phone) {
            setCustomerPhone(customer.phone)
            setPhoneNumber(customer.phone)
          }
        }
      } catch (error) {
        console.error('Error fetching customer phone:', error)
      }
    }

    fetchCustomerPhone()
  }, [jobId])

  const handleConfirmation = async () => {
    if (!paymentMethod) {
      setError('Please select the payment method you received')
      return
    }

    if (paymentMethod === 'mpesa' && !phoneNumber.trim()) {
      setError('Please enter the customer phone number')
      return
    }

    setProcessing(true)
    setError(null)

    try {
      // Generate receipt reference
      const reference = `MK${Date.now().toString().slice(-6)}`

      // Generate receipt data
      const receipt = {
        reference: reference,
        customer_name: customerName,
        customer_phone: phoneNumber,
        tower_name: profile?.full_name,
        amount: amount,
        payment_method: paymentMethod,
        confirmed_at: new Date().toISOString(),
        job_id: jobId
      }

      // Update job status to payment_completed
      const { error: updateError } = await supabase
        .from('tow_jobs')
        .update({
          status: 'payment_completed'
        })
        .eq('id', jobId)

      if (updateError) {
        console.error('Error updating job:', updateError)
        const errorMessage = updateError.message || 'Failed to update job status'
        throw new Error(errorMessage)
      }

      // Try to update payment_method if column exists
      try {
        const { error: pmError } = await supabase
          .from('tow_jobs')
          .update({ payment_method: paymentMethod })
          .eq('id', jobId)
        if (pmError) {
          console.log('payment_method column might not exist yet:', pmError.message)
        }
      } catch (e: any) {
        console.log('payment_method column might not exist yet:', e.message)
      }

      // Try to update paid_at if column exists
      try {
        const { error: paError } = await supabase
          .from('tow_jobs')
          .update({ paid_at: new Date().toISOString() })
          .eq('id', jobId)
        if (paError) {
          console.log('paid_at column might not exist yet:', paError.message)
        }
      } catch (e: any) {
        console.log('paid_at column might not exist yet:', e.message)
      }

      // Try to update confirmed_by if column exists
      try {
        const { error: cbError } = await supabase
          .from('tow_jobs')
          .update({ confirmed_by: profile?.id })
          .eq('id', jobId)
        if (cbError) {
          console.log('confirmed_by column might not exist yet:', cbError.message)
        }
      } catch (e: any) {
        console.log('confirmed_by column might not exist yet:', e.message)
      }

      // Store receipt in database
      const { error: receiptError } = await supabase.from('receipts').insert({
        job_id: jobId,
        customer_id: (await supabase.from('tow_jobs').select('customer_id').eq('id', jobId).single()).data?.customer_id,
        tower_id: profile?.id,
        reference: reference,
        amount: amount,
        payment_method: paymentMethod,
        customer_name: customerName,
        customer_phone: phoneNumber,
        tower_name: profile?.full_name,
        issued_at: new Date().toISOString(),
        confirmed_by: profile?.id
      })

      if (receiptError) {
        console.error('Error inserting receipt:', receiptError)
        // Continue anyway - the job is marked as paid
      }

      // Notify customer that payment is confirmed
      const { error: notificationError } = await supabase.from('notifications').insert({
        recipient_id: (await supabase.from('tow_jobs').select('customer_id').eq('id', jobId).single()).data?.customer_id,
        type: 'payment_confirmed',
        title: 'Payment Confirmed',
        message: `Your payment of KES ${amount.toLocaleString()} has been confirmed. Your receipt is now available for download.`,
        job_id: jobId,
        created_at: new Date().toISOString()
      })

      if (notificationError) {
        console.error('Error inserting notification:', notificationError)
        // Continue anyway - the job is marked as paid
      }

      setReceiptData(receipt)
      setConfirmed(true)

      // Close modal immediately after confirmation
      setTimeout(() => {
        onConfirmationComplete()
        onClose()
      }, 2000)

    } catch (error: any) {
      console.error('Payment confirmation error:', error)
      const errorMessage = error?.message || 'Failed to confirm payment. Please try again.'
      setError(errorMessage)
    } finally {
      setProcessing(false)
    }
  }

  const downloadReceipt = () => {
    if (!receiptData) return

    const doc = new jsPDF()
    const pageWidth = doc.internal.pageSize.getWidth()
    const pageHeight = doc.internal.pageSize.getHeight()

    // Header with branding
    doc.setFillColor(220, 38, 38)
    doc.rect(0, 0, pageWidth, 50, 'F')

    // Logo placeholder
    doc.setFillColor(255, 255, 255)
    doc.circle(30, 25, 12, 'F')
    doc.setTextColor(220, 38, 38)
    doc.setFontSize(14)
    doc.setFont('helvetica', 'bold')
    doc.text('Mk', 30, 29, { align: 'center' })

    // Company name and tagline
    doc.setTextColor(255, 255, 255)
    doc.setFontSize(20)
    doc.setFont('helvetica', 'bold')
    doc.text('Mkononi', 50, 22)
    doc.setFontSize(10)
    doc.setFont('helvetica', 'normal')
    doc.text('One breakdown. One connected journey.', 50, 30)

    // Receipt title
    doc.setFontSize(14)
    doc.setFont('helvetica', 'bold')
    doc.text('OFFICIAL RECEIPT', pageWidth - 20, 35, { align: 'right' })

    // Divider line
    doc.setDrawColor(220, 38, 38)
    doc.setLineWidth(2)
    doc.line(20, 55, pageWidth - 20, 55)

    // Receipt number and date
    doc.setTextColor(0, 0, 0)
    doc.setFontSize(10)
    doc.setFont('helvetica', 'normal')
    doc.text(`Receipt #: ${receiptData.reference}`, 20, 65)
    doc.text(`Date: ${new Date(receiptData.confirmed_at).toLocaleDateString()}`, pageWidth - 20, 65, { align: 'right' })

    // Customer info section
    doc.setFillColor(245, 245, 245)
    doc.roundedRect(20, 75, pageWidth - 40, 30, 3, 3, 'F')

    doc.setFontSize(11)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(220, 38, 38)
    doc.text('CUSTOMER', 25, 85)

    doc.setFontSize(10)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(60, 60, 60)
    doc.text(receiptData.customer_name, 25, 93)
    doc.text(receiptData.customer_phone || 'N/A', 25, 100)

    // Service details section
    let y = 120
    doc.setFontSize(11)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(220, 38, 38)
    doc.text('SERVICE DETAILS', 20, y)
    y += 10

    // Table header
    doc.setFillColor(220, 38, 38)
    doc.rect(20, y, pageWidth - 40, 8, 'F')
    doc.setTextColor(255, 255, 255)
    doc.setFontSize(9)
    doc.setFont('helvetica', 'bold')
    doc.text('DESCRIPTION', 25, y + 5)
    doc.text('AMOUNT', pageWidth - 40, y + 5, { align: 'right' })

    y += 8
    doc.setDrawColor(200, 200, 200)
    doc.line(20, y, pageWidth - 20, y)

    // Service row
    y += 5
    doc.setTextColor(0, 0, 0)
    doc.setFontSize(10)
    doc.setFont('helvetica', 'normal')
    doc.text('TOWING SERVICE', 25, y + 5)
    doc.text(`KES ${receiptData.amount.toLocaleString()}`, pageWidth - 40, y + 5, { align: 'right' })

    y += 10
    doc.setDrawColor(200, 200, 200)
    doc.line(20, y, pageWidth - 20, y)

    // Total
    y += 8
    doc.setFontSize(12)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(220, 38, 38)
    doc.text('TOTAL', 25, y + 5)
    doc.text(`KES ${receiptData.amount.toLocaleString()}`, pageWidth - 40, y + 5, { align: 'right' })

    y += 15
    doc.setDrawColor(200, 200, 200)
    doc.line(20, y, pageWidth - 20, y)

    // Payment method
    y += 10
    doc.setFontSize(10)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(60, 60, 60)
    doc.text(`Payment Method: ${receiptData.payment_method.toUpperCase()}`, 25, y)
    doc.text(`Payment Status: PAID`, 25, y + 7)
    doc.text(`Confirmed At: ${new Date(receiptData.confirmed_at).toLocaleString()}`, 25, y + 14)
    doc.text(`Confirmed By: ${receiptData.tower_name}`, 25, y + 21)
    
    // Locations section
    y += 20
    doc.setFontSize(11)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(220, 38, 38)
    doc.text('LOCATIONS', 20, y)
    y += 10
    
    doc.setFontSize(9)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(60, 60, 60)
    doc.text('Pickup Location:', 25, y)
    y += 6
    doc.setFontSize(8)
    doc.setTextColor(100, 100, 100)
    doc.text('Theta ward, Juja, Kiambu, 01001, Kenya', 25, y)
    
    // Service notes
    y += 15
    doc.setFontSize(11)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(220, 38, 38)
    doc.text('SERVICE NOTES', 20, y)
    y += 10
    
    doc.setFontSize(9)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(60, 60, 60)
    doc.text('towing service request', 25, y)
    
    // Service provider section
    y += 20
    doc.setFontSize(11)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(220, 38, 38)
    doc.text('SERVICE PROVIDER', 20, y)
    y += 10
    
    doc.setFontSize(10)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(60, 60, 60)
    doc.text(receiptData.tower_name, 25, y)
    y += 6
    doc.setFontSize(9)
    doc.setTextColor(100, 100, 100)
    doc.text('Tower', 25, y)
    y += 6
    doc.text('Vehicle: KDA 100A', 25, y)

    // Locations section
    y += 20
    doc.setFontSize(11)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(220, 38, 38)
    doc.text('LOCATIONS', 20, y)
    y += 10

    doc.setFontSize(9)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(60, 60, 60)
    doc.text('Pickup Location:', 25, y)
    y += 6
    doc.setFontSize(8)
    doc.setTextColor(100, 100, 100)
    doc.text('Theta ward, Juja, Kiambu, 01001, Kenya', 25, y)

    // Service notes
    y += 15
    doc.setFontSize(11)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(220, 38, 38)
    doc.text('SERVICE NOTES', 20, y)
    y += 10

    doc.setFontSize(9)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(60, 60, 60)
    doc.text('towing service request', 25, y)

    // Service provider section
    y += 20
    doc.setFontSize(11)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(220, 38, 38)
    doc.text('SERVICE PROVIDER', 20, y)
    y += 10

    doc.setFontSize(10)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(60, 60, 60)
    doc.text(receiptData.tower_name, 25, y)
    y += 6
    doc.setFontSize(9)
    doc.setTextColor(100, 100, 100)
    doc.text('Tower', 25, y)
    y += 6
    doc.text('Vehicle: KDA 100A', 25, y)

    // Footer
    y += 20
    y += 20
    doc.setDrawColor(220, 38, 38)
    doc.line(20, y, pageWidth - 20, y)

    y += 10
    doc.setFontSize(9)
    doc.setFont('helvetica', 'normal')
    doc.setTextColor(100, 100, 100)
    doc.text('Thank you for choosing Mkononi. Your satisfaction is our priority.', 20, y)
    y += 6
    doc.text(`Generated on ${new Date(receiptData.confirmed_at).toLocaleString()}`, 20, y)
    y += 8
    doc.setFontSize(8)
    doc.setTextColor(150, 150, 150)
    doc.text('This is an official receipt. For inquiries, contact support@mkononi.co.ke', 20, y)

    // Save the PDF
    doc.save(`Mkononi_Receipt_${receiptData.reference}.pdf`)
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '1.5rem',
        maxWidth: '500px',
        width: '100%',
        margin: '0 auto',
        maxHeight: '90vh',
        overflowY: 'auto'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>Mark Job as Paid</h2>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.5rem' }}
          >
            <X size={20} color="var(--muted)" />
          </button>
        </div>

        {error && (
          <div style={{
            background: '#fee2e2',
            border: '1px solid #fecaca',
            color: '#dc2626',
            padding: '0.75rem',
            borderRadius: '8px',
            marginBottom: '1rem',
            fontSize: '0.9rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <AlertCircle size={16} />
            {error}
          </div>
        )}

        {confirmed ? (
          <div style={{ textAlign: 'center', padding: '2rem 0' }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #10b981, #059669)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem'
            }}>
              <CheckCircle size={40} color="white" />
            </div>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '0.5rem' }}>Payment Confirmed!</h3>
            <p style={{ color: 'var(--muted)', lineHeight: 1.6, marginBottom: '1.5rem' }}>
              Receipt has been generated and sent to the customer.
            </p>

            {receiptData && (
              <div style={{
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1.5rem',
                textAlign: 'left'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                  <Receipt size={18} color="var(--primary)" />
                  <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>Receipt Generated</span>
                </div>
                <div style={{ fontSize: '0.85rem', color: 'var(--muted)', marginBottom: '0.5rem' }}>
                  <strong>Reference:</strong> {receiptData.reference}
                </div>
                <div style={{ fontSize: '0.85rem', color: 'var(--muted)', marginBottom: '0.5rem' }}>
                  <strong>Amount:</strong> KES {receiptData.amount.toLocaleString()}
                </div>
                <div style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>
                  <strong>Status:</strong> <span style={{ color: '#10b981', fontWeight: 600 }}>CONFIRMED</span>
                </div>
              </div>
            )}

            <button
              onClick={downloadReceipt}
              style={{
                width: '100%',
                padding: '0.75rem',
                borderRadius: '8px',
                background: 'white',
                border: '1px solid #e2e8f0',
                color: 'var(--text)',
                fontSize: '0.9rem',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                marginBottom: '0.75rem'
              }}
            >
              <Download size={18} />
              Download Receipt
            </button>

            <div style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>
              Redirecting to dashboard...
            </div>
          </div>
        ) : (
          <>
            {/* Info Banner */}
            <div style={{
              background: '#eff6ff',
              border: '1px solid #bfdbfe',
              borderRadius: '8px',
              padding: '1rem',
              marginBottom: '1.5rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <Info size={18} color="#3b82f6" />
                <span style={{ fontWeight: 600, fontSize: '0.9rem', color: '#1e40af' }}>Important</span>
              </div>
              <div style={{ fontSize: '0.85rem', color: '#1e40af', lineHeight: 1.5 }}>
                Only confirm if you have actually received payment from the customer. This will generate an official receipt for their insurance claim.
              </div>
            </div>

            {/* Payment Details */}
            <div style={{
              background: 'linear-gradient(135deg, var(--blue-light), var(--primary-light))',
              border: '1px solid var(--primary-light)',
              borderRadius: '12px',
              padding: '1.5rem',
              marginBottom: '1.5rem'
            }}>
              <div style={{ fontSize: '0.9rem', color: 'var(--muted)', marginBottom: '0.5rem' }}>
                Payment from {customerName}
              </div>
              <div style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--primary)', marginBottom: '0.5rem' }}>
                KES {amount.toLocaleString()}
              </div>
            </div>

            {/* Payment Method Selection */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: 600, marginBottom: '0.75rem' }}>
                Select Payment Method Received
              </label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <button
                  onClick={() => setPaymentMethod('cash')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    justifyContent: 'flex-start',
                    padding: '0.75rem 1rem',
                    borderRadius: '8px',
                    border: paymentMethod === 'cash' ? '2px solid #f59e0b' : '1px solid #e5e7eb',
                    background: paymentMethod === 'cash' ? '#fef3c7' : 'white',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  <Wallet size={20} color={paymentMethod === 'cash' ? '#f59e0b' : 'var(--muted)'} />
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontSize: '0.9rem', fontWeight: 600 }}>Cash</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>Received in cash</div>
                  </div>
                  {paymentMethod === 'cash' && <CheckCircle size={20} color="#f59e0b" style={{ marginLeft: 'auto' }} />}
                </button>

                <button
                  onClick={() => setPaymentMethod('mpesa')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    justifyContent: 'flex-start',
                    padding: '0.75rem 1rem',
                    borderRadius: '8px',
                    border: paymentMethod === 'mpesa' ? '2px solid #10b981' : '1px solid #e5e7eb',
                    background: paymentMethod === 'mpesa' ? '#f0fdf4' : 'white',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  <Smartphone size={20} color={paymentMethod === 'mpesa' ? '#10b981' : 'var(--muted)'} />
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontSize: '0.9rem', fontWeight: 600 }}>M-Pesa</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>Received via M-Pesa</div>
                  </div>
                  {paymentMethod === 'mpesa' && <CheckCircle size={20} color="#10b981" style={{ marginLeft: 'auto' }} />}
                </button>

                <button
                  onClick={() => setPaymentMethod('transfer')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    justifyContent: 'flex-start',
                    padding: '0.75rem 1rem',
                    borderRadius: '8px',
                    border: paymentMethod === 'transfer' ? '2px solid var(--primary)' : '1px solid #e5e7eb',
                    background: paymentMethod === 'transfer' ? 'var(--primary-light)' : 'white',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  <CreditCard size={20} color={paymentMethod === 'transfer' ? 'var(--primary)' : 'var(--muted)'} />
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontSize: '0.9rem', fontWeight: 600 }}>Bank Transfer</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>Received via bank transfer</div>
                  </div>
                  {paymentMethod === 'transfer' && <CheckCircle size={20} color="var(--primary)" style={{ marginLeft: 'auto' }} />}
                </button>
              </div>
            </div>

            {paymentMethod === 'mpesa' && (
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                  Customer Phone Number (for record)
                </label>
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="e.g., 0712 345 678"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '8px',
                    border: '1px solid #d1d5db',
                    fontSize: '1rem',
                    outline: 'none'
                  }}
                />
                {customerPhone && (
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                    Customer registered phone: {customerPhone}
                  </div>
                )}
              </div>
            )}

            {/* Warning */}
            <div style={{
              background: '#fef3c7',
              border: '1px solid #fcd34d',
              borderRadius: '8px',
              padding: '1rem',
              marginBottom: '1.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              <Shield size={20} color="#d97706" />
              <div style={{ fontSize: '0.85rem', color: '#92400e', lineHeight: 1.5 }}>
                <strong>Disclaimer:</strong> Confirming payment implies you have received the full amount. This receipt is legally binding for insurance purposes.
              </div>
            </div>

            <button
              onClick={handleConfirmation}
              disabled={processing}
              style={{
                width: '100%',
                padding: '1rem',
                borderRadius: '12px',
                background: processing ? '#9ca3af' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: 'white',
                border: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: processing ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
            >
              {processing ? (
                <>
                  <Loader2 className="spin" size={20} />
                  Confirming...
                </>
              ) : (
                <>
                  <CheckCircle size={20} />
                  Confirm Payment & Generate Receipt
                </>
              )}
            </button>

            <div style={{
              marginTop: '1rem',
              textAlign: 'center',
              fontSize: '0.8rem',
              color: 'var(--muted)'
            }}>
              By confirming, you acknowledge receipt of payment
            </div>
          </>
        )}
      </div>
    </div>
  )
}
